<template>
  <div class="conditional-test-runner">
    <!-- Preview Mode Banner -->
    <div v-if="previewMode" class="preview-banner">
      🔀 MODE PREVIEW - Test Conditionnel
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="container mt-5">
      <div class="alert alert-info">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        Chargement du test conditionnel...
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="container mt-5">
      <div class="alert alert-danger">
        <h4>❌ Erreur</h4>
        <p>{{ error }}</p>
        <button @click="goBack" class="btn btn-secondary mt-2">
          ← Retour aux publications
        </button>
      </div>
    </div>

    <!-- Test terminated -->
    <div v-else-if="isTerminated">
      <TopNavbar
        :testData="testData"
        :publicationData="publicationStore.publicationData"
        :stopTimer="stopTimer"
      />
      <div class="container mt-5">
        <LastPage
          :testData="testData"
          :testType="'conditional'"
          :terminationData="terminationData"
          :conditionalData="{
            pathTaken,
            nodeResponses,
            finalScore: currentScore,
            finalMaxScore: currentMaxScore
          }"
        />
      </div>
    </div>

    <!-- Active test -->
    <div v-else-if="currentElement || isPageNode">
      <ConditionalRenderer
        :currentElement="currentElement"
        :testData="testData"
        :currentNodeData="getCurrentNode?.data"
        :onSubmitAnswer="handleAnswer"
        :stopTimer="stopTimer"
        :previewMode="previewMode"
        :isPageNode="isPageNode"
        :pageNode="isPageNode ? getCurrentNode : null"
        :pageChildNodes="isPageNode ? getPageChildren : []"
        :pageChildElements="isPageNode ? getPageChildElements : []"
      />
    </div>

    <!-- Fallback -->
    <div v-else class="container mt-5">
      <div class="alert alert-warning">
        ⚠️ Aucun élément à afficher
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { useResponsesStore } from '@/stores/useResponsesStore';
import { usePublicationStore } from '@/stores/usePublicationStore';
import { useTestDataLoader } from '@/testRenderUtil/useTestDataLoader';
import { useConditionalNavigation } from '@/testRenderUtil/useConditionalNavigation';
import { useSessionManager } from '@/testRenderUtil/useSessionManager';
import { useResultSender } from '@/testRenderUtil/useResultSender';
import ConditionalRenderer from './ConditionalRenderer.vue';
import LastPage from './LastPage.vue';
import TopNavbar from './topnavbar.vue';

// Props pour le mode preview
const props = defineProps({
  testData: Object,
  previewMode: {
    type: Boolean,
    default: false
  },
  readOnly: {
    type: Boolean,
    default: false
  }
});

const route = useRoute();
const router = useRouter();
const responsesStore = useResponsesStore();
const publicationStore = usePublicationStore();

// Route params
const testId = ref(route.query.testId);
const publicationId = route.query.publicationId;
const groupId = route.query.groupId;

// State
const loading = ref(true);
const error = ref(null);
const stopTimer = ref(false);
const conditionalStructure = ref(null);
const isTerminated = ref(false);
const terminationData = ref(null);

// Get user ID from token
const token = localStorage.getItem('token');
let userId = null;
if (token) {
  try {
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    userId = decodedToken?._id || decodedToken?.sub;
  } catch (e) {
    console.error("Token decoding failed:", e);
  }
}

// Composables - testData peut venir des props (preview) ou être chargé
const { testData: loadedTestData, fetchTestData } = useTestDataLoader(testId);
const testData = props.previewMode && props.testData ? ref(props.testData) : loadedTestData;
const {
  currentNodeId,
  pathTaken,
  nodeResponses,
  currentScore,
  currentMaxScore,
  getCurrentNode,
  getCurrentElement,
  isTerminationNode,
  getTerminationData,
  isPageNode,
  getPageChildren,
  getPageChildElements,
  initializeNavigation,
  findNextNode,
  navigateToNode,
  recordResponse,
  calculateScore
} = useConditionalNavigation(conditionalStructure, testData);

let sessionManager = null;
const { sendResultsToDatabase } = useResultSender(responsesStore);

// Computed
const currentElement = computed(() => getCurrentElement.value);

/**
 * Charge la structure conditionnelle depuis l'API
 */
const loadConditionalStructure = async () => {
  try {
    console.log('🔀 Chargement de la structure conditionnelle pour test:', testId.value);
    console.log('🎬 Mode preview:', props.previewMode);

    let response;

    // En mode preview, utiliser la route preview avec le token
    if (props.previewMode) {
      const previewToken = route.query.previewToken;
      console.log('🎬 Utilisation de la route preview avec token');
      response = await api.get(`/conditional-tests/${testId.value}/structure/preview?previewToken=${previewToken}`);
    } else {
      // Mode normal avec authentification
      response = await api.get(`/conditional-tests/${testId.value}/structure`);
    }

    console.log('📦 Réponse reçue:', response.data);

    if (!response.data.success) {
      console.error('❌ Structure non chargée:', response.data);
      throw new Error(response.data.message || 'Structure conditionnelle non disponible');
    }

    conditionalStructure.value = response.data.ConditionalStructure;

    if (!conditionalStructure.value) {
      console.error('❌ ConditionalStructure est null/undefined dans la réponse');
      throw new Error('Structure conditionnelle vide dans la réponse');
    }

    // 🎨 Charger les displaySettings si présents
    if (response.data.displaySettings) {
      console.log('🎨 Display settings reçus:', response.data.displaySettings);
      testData.value.displaySettings = response.data.displaySettings;
    }

    console.log('✅ Structure conditionnelle chargée:', {
      nodes: conditionalStructure.value.nodes.length,
      edges: conditionalStructure.value.edges.length,
      startNode: conditionalStructure.value.startNode,
      displaySettings: !!response.data.displaySettings
    });

    // 🔍 DEBUG: Log mode values received from API
    conditionalStructure.value.nodes.forEach(node => {
      if (node.data?.mode !== undefined) {
        console.log(`📊 Frontend received Node ${node.id.substring(0, 8)} (${node.type}): mode="${node.data.mode}" (type: ${typeof node.data.mode})`);
      }
    });

    return true;
  } catch (err) {
    console.error('❌ Erreur chargement structure:', err);
    error.value = err.response?.data?.message || err.message || 'Erreur lors du chargement de la structure conditionnelle';
    return false;
  }
};

/**
 * Gère les réponses d'un PageNode et navigue au node suivant
 */
const handlePageNodeAnswer = async (pageAnswer) => {
  console.log('📄 Handling PageNode answer:', pageAnswer);

  const pageNode = getCurrentNode.value;
  const { childResponses } = pageAnswer;

  if (!pageNode || pageNode.type !== 'page') {
    console.error('❌ PageNode invalide');
    return;
  }

  // Traiter chaque réponse enfant
  for (const childResponse of childResponses) {
    const { nodeId, response } = childResponse;

    if (!response) {
      console.log('⚠️ Pas de réponse pour le nested node:', nodeId);
      continue;
    }

    // Trouver le node enfant
    const childNode = conditionalStructure.value.nodes.find(n => n.id === nodeId);
    if (!childNode) {
      console.error('❌ Nested node introuvable:', nodeId);
      continue;
    }

    // Récupérer l'élément associé
    const element = testData.value.elements?.find(el => el.el_ID === childNode.data?.elementId);
    if (!element) {
      console.error('❌ Element introuvable pour nested node:', nodeId);
      continue;
    }

    // Calculer le score pour ce nested node
    const { score, maxScore } = calculateScore(element, response);

    // Enregistrer la réponse (score comptabilisé si le nested node est en mode score)
    recordResponse(nodeId, element.el_ID, response, score, maxScore);

    // Enregistrer dans le store pour compatibilité
    responsesStore.saveResponse({
      userId: userId,
      questionId: element.el_ID,
      type: element.el_Type,
      answered: true,
      selectedOptions: response.selectedOptions,
      score,
      maxScore,
      questionLabel: element.el_Label || element.el_Text
    });

    console.log(`✅ Nested node ${nodeId.substring(0, 8)} processed: ${score}/${maxScore}`);
  }

  // Sauvegarder de manière continue
  await saveProgressToDatabase();

  // Trouver le prochain node (PageNode utilise une navigation simple sans conditions)
  const nextNodeId = findNextNode(null, null);

  if (!nextNodeId) {
    error.value = 'Impossible de déterminer le prochain node après PageNode.';
    return;
  }

  console.log('📄 PageNode → navigation vers:', nextNodeId);

  // Naviguer vers le prochain node
  const navigated = navigateToNode(nextNodeId);

  if (!navigated) {
    error.value = 'Erreur de navigation';
    return;
  }

  // Vérifier si c'est un node de terminaison
  if (isTerminationNode.value) {
    await handleTermination();
    return;
  }

  // Si c'est un switcher, l'évaluer immédiatement
  const newNode = getCurrentNode.value;
  if (newNode && newNode.type === 'switcher') {
    await handleSwitcherNode();
  }

  // Si c'est un start node (ne devrait pas arriver)
  if (newNode && newNode.type === 'start') {
    await handleStartNode();
  }
};

/**
 * Gère la réponse de l'étudiant et navigue au node suivant
 */
const handleAnswer = async (answer) => {
  console.log('📝 Handling answer:', answer);

  const node = getCurrentNode.value;

  if (!node) {
    console.error('❌ Node manquant');
    return;
  }

  // Si c'est un PageNode avec des réponses d'enfants
  if (answer.isPageNode && answer.childResponses) {
    await handlePageNodeAnswer(answer);
    return;
  }

  const element = currentElement.value;

  if (!element) {
    console.error('❌ Element manquant');
    return;
  }

  // Si le node est de type start, naviguer automatiquement
  if (node.type === 'start') {
    await handleStartNode();
    return;
  }

  // Si le node est de type switcher, naviguer automatiquement
  if (node.type === 'switcher') {
    await handleSwitcherNode();
    return;
  }

  // Si c'est un message, passer automatiquement au suivant (pas de scoring)
  if (element.el_Type === 'message') {
    console.log('💬 Message node - passage automatique au suivant');

    // Trouver l'edge sortant (les messages ont un seul output)
    const edges = conditionalStructure.value.edges.filter(e => e.source === node.id);

    if (edges.length === 0) {
      error.value = 'Aucun node suivant après le message';
      return;
    }

    const nextNodeId = edges[0].target;
    navigateToNode(nextNodeId);

    // Vérifier le type du nouveau node
    const newNode = getCurrentNode.value;
    if (newNode?.type === 'switcher') {
      await handleSwitcherNode();
    } else if (newNode?.type === 'start') {
      await handleStartNode();
    } else if (isTerminationNode.value) {
      await handleTermination();
    }

    return;
  }

  // Calculer le score
  const { score, maxScore } = calculateScore(element, answer);

  // Enregistrer la réponse
  recordResponse(node.id, element.el_ID, answer, score, maxScore);

  // Enregistrer dans le store pour compatibilité
  responsesStore.saveResponse({
    userId: userId,
    questionId: element.el_ID,
    type: element.el_Type,
    answered: true,
    selectedOptions: answer.selectedOptions,
    score,
    maxScore,
    questionLabel: element.el_Label || element.el_Text
  });

  // Sauvegarder de manière continue
  await saveProgressToDatabase();

  // Trouver le prochain node
  const nextNodeId = findNextNode(answer, score);

  if (!nextNodeId) {
    error.value = 'Impossible de déterminer le prochain node. Veuillez contacter l\'administrateur.';
    return;
  }

  // Naviguer vers le prochain node
  const navigated = navigateToNode(nextNodeId);

  if (!navigated) {
    error.value = 'Erreur de navigation';
    return;
  }

  // Vérifier si c'est un node de terminaison
  if (isTerminationNode.value) {
    await handleTermination();
    return;
  }

  // Si c'est un switcher, l'évaluer immédiatement (invisible pour l'étudiant)
  const newNode = getCurrentNode.value;
  if (newNode && newNode.type === 'switcher') {
    await handleSwitcherNode();
  }
};

/**
 * Gère un node de type start (passage automatique au node suivant)
 */
const handleStartNode = async () => {
  console.log('🎬 Handling start node');

  const startNode = getCurrentNode.value;
  if (!startNode || startNode.type !== 'start') {
    console.error('❌ Node n\'est pas un start node');
    return;
  }

  // Trouver l'edge sortant du start node
  const edges = conditionalStructure.value.edges.filter(e => e.source === startNode.id);

  if (edges.length === 0) {
    error.value = 'Aucun node suivant trouvé après le start node';
    return;
  }

  // Prendre le premier edge (le start node n'a qu'une seule sortie normalement)
  const nextNodeId = edges[0].target;

  console.log('🎬 Start node → navigation vers:', nextNodeId);

  // Naviguer vers le node suivant
  navigateToNode(nextNodeId);

  // Vérifier si c'est un switcher ou un autre node spécial
  const newNode = getCurrentNode.value;
  if (newNode?.type === 'switcher') {
    await handleSwitcherNode();
  } else if (newNode?.type === 'start') {
    // Ne devrait pas arriver, mais par sécurité
    console.error('❌ Start node pointe vers un autre start node');
  } else if (isTerminationNode.value) {
    await handleTermination();
  }
};

/**
 * Gère un node de type switcher (redirection invisible)
 */
const handleSwitcherNode = async () => {
  console.log('🔀 Handling switcher node');

  const switcherNode = getCurrentNode.value;
  if (!switcherNode || switcherNode.type !== 'switcher') {
    console.error('❌ Node n\'est pas un switcher');
    return;
  }

  // Évaluer le switcher pour trouver la destination
  const nextNodeId = findNextNode(null, currentScore.value);

  if (!nextNodeId) {
    error.value = 'Erreur dans l\'évaluation du switcher';
    return;
  }

  // Naviguer vers la destination
  navigateToNode(nextNodeId);

  // Vérifier si terminaison
  if (isTerminationNode.value) {
    await handleTermination();
  }
};

/**
 * Gère l'arrivée sur un node de terminaison
 */
const handleTermination = async () => {
  console.log('🏁 Test terminé');

  stopTimer.value = true;
  isTerminated.value = true;
  terminationData.value = getTerminationData.value;

  // Appliquer le bonus de terminaison au score
  if (terminationData.value.bonus) {
    currentScore.value += terminationData.value.bonus;
    console.log('🎁 Bonus de terminaison appliqué:', terminationData.value.bonus);
  }

  // Debug: vérifier les données avant d'afficher LastPage
  console.log('📊 Données de terminaison:', {
    pathTaken: pathTaken.value,
    nodeResponses: nodeResponses.value,
    currentScore: currentScore.value,
    currentMaxScore: currentMaxScore.value,
    terminationData: terminationData.value
  });

  // Envoyer les résultats finaux
  await sendFinalResults();

  // Terminer la session
  await sessionManager?.endSession(false);

  // Incrémenter les tentatives
  try {
    await api.patch(`/publications/${publicationId}/increment-attempts`);
  } catch (e) {
    console.error("Erreur incrément publication:", e);
  }
};

/**
 * Sauvegarde continue des progrès
 */
const saveProgressToDatabase = async () => {
  // TODO: Implémenter sauvegarde continue si nécessaire
  console.log('💾 Sauvegarde des progrès...');
};

/**
 * Envoie les résultats finaux à la base de données
 */
const sendFinalResults = async () => {
  // Ne pas envoyer de résultats en mode preview
  if (props.previewMode) {
    console.log('🎬 Mode preview - Pas d\'envoi de résultats');
    return;
  }

  console.log('📤 Envoi des résultats finaux');

  await sendResultsToDatabase({
    userId,
    testId: testId.value,
    publicationId,
    groupId,
    owner: publicationStore.publicationData?.owner,
    testData: testData.value,
    conditionalData: {
      pathTaken: pathTaken.value,
      nodeResponses: nodeResponses.value,
      finalScore: currentScore.value,
      finalMaxScore: currentMaxScore.value,
      terminationNodeId: terminationData.value?.nodeId,
      terminationBonus: terminationData.value?.bonus || 0
    }
  });
};

/**
 * Gère le timeout du test
 */
const onTimeUp = async () => {
  console.log('⏰ Temps écoulé');
  await handleTermination();
};

/**
 * Retour aux publications
 */
const goBack = () => {
  router.push('/publications');
};

/**
 * Initialisation au montage
 */
onMounted(async () => {
  console.log('🔀 ConditionalTestRunner mounted');
  console.log('🎬 Preview mode:', props.previewMode);

  // En mode preview, testData est fourni via props
  if (props.previewMode && props.testData) {
    console.log('🎬 Mode preview - utilisation des props testData');
    // testData est déjà défini via props, pas besoin de charger
  } else {
    // Mode normal - charger les données du test
    await fetchTestData();

    if (!testData.value) {
      error.value = 'Test non trouvé';
      loading.value = false;
      return;
    }
  }

  // Vérifier que c'est bien un test conditionnel
  if (testData.value.progression !== 'conditional') {
    error.value = `Ce test n'est pas de type conditionnel (type actuel: ${testData.value.progression})`;
    loading.value = false;
    return;
  }

  // Charger la structure conditionnelle
  const structureLoaded = await loadConditionalStructure();
  if (!structureLoaded) {
    loading.value = false;
    return;
  }

  // En mode preview, ne pas gérer les sessions ni publications
  if (!props.previewMode) {
    // Charger la publication
    if (publicationId) {
      await publicationStore.fetchPublication(publicationId);
    }

    const publicationAccess = publicationStore.publicationData?.access;

    // Initialiser le session manager
    sessionManager = useSessionManager(
      testId,
      publicationId,
      groupId,
      userId,
      publicationAccess
    );

    if (token && userId) {
      await sessionManager.startSession();
    }
  } else {
    console.log('🎬 Mode preview - Session manager désactivé');
  }

  // Initialiser la navigation
  const initialized = initializeNavigation();
  if (!initialized) {
    error.value = 'Erreur d\'initialisation de la navigation';
    loading.value = false;
    return;
  }

  // Si le premier node est un start node, passer automatiquement au suivant
  if (getCurrentNode.value?.type === 'start') {
    console.log('🎬 Start node détecté, navigation automatique vers le premier node');
    await handleStartNode();
  }

  // Si le premier node est un switcher, l'évaluer immédiatement
  if (getCurrentNode.value?.type === 'switcher') {
    await handleSwitcherNode();
  }

  // Si c'est un PageNode, on l'affiche normalement (pas de navigation auto)
  if (getCurrentNode.value?.type === 'page') {
    console.log('📄 PageNode détecté - affichage normal');
  }

  loading.value = false;

  // Event listener pour fermeture fenêtre
  window.addEventListener('beforeunload', onBeforeUnload);
});

/**
 * Nettoyage au démontage
 */
onUnmounted(() => {
  window.removeEventListener('beforeunload', onBeforeUnload);
});

/**
 * Gère la fermeture de la fenêtre (abandon)
 */
const onBeforeUnload = async (event) => {
  if (!isTerminated.value) {
    try {
      await sessionManager?.endSession(true); // abandon
    } catch (e) {
      console.warn('Abandon non enregistré à temps');
    }
  }
};
</script>

<style scoped>
.conditional-test-runner {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.spinner-border {
  margin-right: 10px;
}

/* Preview mode banner */
.preview-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 20px;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  z-index: 9999;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  letter-spacing: 1px;
}
</style>
